import React, { useState, useEffect } from 'react';
import { FaCrown, FaStar, FaFire, FaGem, FaBolt, FaRocket } from 'react-icons/fa';
import { getLevelName, getLevelTier, LEVEL_CONFIG, LEVEL_MILESTONES, LEVEL_NAMES } from '../../data/candyConfig';
import { useRadixConnect } from '../../../radix_connect';
import {
  RADIX_API_GATEWAY_URL,
  CANDY_MACHINE_COMPONENT_ADDRESS,
  GBOF_RESOURCE_ADDRESS,
} from '../../../constants';
import './LevelProgress.css';

/**
 * LevelProgress - Visual display of user level and GBOF deposit progress
 * Shows current level, progress to next level, and GBOF requirements
 *
 * Updated for v2 CandyDispenser:
 * - Level up when deposited_gbof equals current_level
 * - Cost to reach level N from N-1 = N GBOF
 * - Max level is 11
 */
const LevelProgress = ({
  level = 0,
  depositedGbof = 0,
  maxLevel = 11,
  onDepositGbof,
  isLevelingUp = false,
  gbofBalance = 0, // User's available GBOF tokens
  gbofNeededForNextLevel = 1
}) => {
  const { selectedAccount } = useRadixConnect();

  // State for fetched total GBOF deposited
  const [totalGbofDeposited, setTotalGbofDeposited] = useState(0);
  const [isLoadingTotal, setIsLoadingTotal] = useState(false);

  // Fetch total GBOF deposited by user into the Candy Machine
  useEffect(() => {
    const fetchTotalGbofDeposited = async () => {
      if (!selectedAccount?.address) {
        setTotalGbofDeposited(0);
        return;
      }

      setIsLoadingTotal(true);

      try {
        let totalDeposited = 0;
        let cursor = null;

        do {
          const response = await fetch(
            RADIX_API_GATEWAY_URL + "/stream/transactions",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                limit_per_page: 100,
                cursor,
                opt_ins: {
                  receipt_output: false,
                  balance_changes: true,
                },
                affected_global_entities_filter: [
                  CANDY_MACHINE_COMPONENT_ADDRESS,
                  selectedAccount.address,
                ],
                manifest_accounts_withdrawn_from_filter: [selectedAccount.address],
                manifest_resources_filter: [GBOF_RESOURCE_ADDRESS],
                transaction_status_filter: "Success",
              }),
            }
          );

          if (!response.ok) {
            console.error("Failed to fetch GBOF transactions");
            break;
          }

          const data = await response.json();

          if (data.items) {
            data.items.forEach((tx) => {
              // Check balance changes for GBOF deposits to the component
              const balanceChanges = tx.balance_changes?.fungible_balance_changes || [];

              balanceChanges.forEach((change) => {
                // Look for negative balance change (withdrawal) from user account for GBOF
                if (
                  change.entity_address === selectedAccount.address &&
                  change.resource_address === GBOF_RESOURCE_ADDRESS
                ) {
                  const changeAmount = parseFloat(change.balance_change);
                  // Negative means user sent GBOF (deposited)
                  if (changeAmount < 0) {
                    totalDeposited += Math.abs(changeAmount);
                  }
                }
              });
            });
          }

          cursor = data.next_cursor;
        } while (cursor);

        setTotalGbofDeposited(Math.floor(totalDeposited));
      } catch (error) {
        console.error("Error fetching total GBOF deposited:", error);
        // Fallback to calculated value
        setTotalGbofDeposited((level * (level + 1)) / 2 + depositedGbof);
      } finally {
        setIsLoadingTotal(false);
      }
    };

    fetchTotalGbofDeposited();
  }, [selectedAccount, level, depositedGbof]);

  // GBOF needed is passed from parent (level + 1 based on component logic)
  const gbofNeeded = gbofNeededForNextLevel || (level + 1);

  // Calculate remaining GBOF needed
  const gbofRemaining = Math.max(0, gbofNeeded - depositedGbof);

  // Calculate overall level progression on 0-12 scale
  // Add fractional progress based on depositedGbof toward next level
  const effectiveMax = LEVEL_CONFIG.EFFECTIVE_MAX_LEVEL; // 12
  const fractionalProgress = gbofNeeded > 0 ? depositedGbof / gbofNeeded : 0;
  const overallProgressPercent = level >= effectiveMax
    ? 100
    : ((level + fractionalProgress) / effectiveMax) * 100;

  // Get level tier info (adjusted for 0-11 effective scale)
  // Icons are mapped locally based on tier index
  const getTierWithIcon = (lvl) => {
    const tier = getLevelTier(lvl);
    const icons = {
      1: <FaFire />,
      2: <FaStar />,
      3: <FaBolt />,
      4: <FaGem />,
      5: <FaCrown />,
    };
    return { ...tier, icon: icons[tier.tier] || <FaFire /> };
  };

  const currentTier = getTierWithIcon(level);
  const nextTier = getTierWithIcon(level + 1);
  const isMaxLevel = level >= maxLevel;

  // Use effective 0-12 milestones for display but show 255 as future
  const milestones = LEVEL_MILESTONES;

  return (
    <div className="level-progress-container">
      {/* Main Level Display */}
      <div className="level-main">
        <div className="level-badge" style={{ borderColor: currentTier.color }}>
          <div
            className="badge-glow"
            style={{ background: `radial-gradient(circle, ${currentTier.color}40 0%, transparent 70%)` }}
          />
          <div className="badge-icon" style={{ color: currentTier.color }}>
            {currentTier.icon}
          </div>
          <div className="badge-level">{level}</div>
          <div className="badge-label">LEVEL</div>
        </div>

        <div className="level-info">
          <div className="tier-name" style={{ color: currentTier.color }}>
            {getLevelName(level)}
          </div>
          <div className="total-deposited">
            <FaFire className="gbof-icon" />
            <span>{totalGbofDeposited} GBOF Deposited</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-header">
          <span className="progress-label">
            {level >= effectiveMax ? 'MAX LEVEL REACHED!' : 'Level Progression'}
          </span>
          {level < effectiveMax && (
            <span className="progress-count">
              Level {level} / {effectiveMax}
            </span>
          )}
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${overallProgressPercent}%` }}
            >
              <div className="progress-shine" />
            </div>

            {/* Milestone markers - positioned using EFFECTIVE_MAX_LEVEL for proper spacing */}
            {milestones.map((milestone, index) => {
              const position = (milestone / effectiveMax) * 100;
              const tier = getLevelTier(milestone);
              const isReached = level >= milestone;
              return (
                <div
                  key={milestone}
                  className={`milestone-marker ${isReached ? 'reached' : ''}`}
                  style={{ left: `${position}%` }}
                  title={`Level ${milestone} - ${getLevelName(milestone)}`}
                >
                  <div
                    className="marker-dot"
                    style={{
                      background: isReached ? tier.color : 'hsl(0 0% 15%)',
                      borderColor: tier.color,
                      boxShadow: isReached ? `0 0 8px ${tier.color}50` : 'none'
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Level up requirements */}
        {!isMaxLevel && (
          <div className="level-requirements">
            <div className="requirement-item">
              <FaFire className="req-icon" />
              <span>Deposit <strong>{gbofRemaining}</strong> more GBOF to reach Level {level + 1}</span>
            </div>
          </div>
        )}
      </div>

      {/* Level Up Buttons */}
      <div className="level-action">
        {isMaxLevel ? (
          <div className="max-level-badge">
            <FaCrown />
            <span>MAXIMUM POWER ACHIEVED</span>
            <FaCrown />
          </div>
        ) : (
          <div className="action-buttons">
            {/* Single Deposit Button */}
            <button
              className={`level-up-btn ${isLevelingUp ? 'processing' : ''} ${gbofBalance < 1 ? 'insufficient' : ''}`}
              onClick={() => onDepositGbof && onDepositGbof(1)}
              disabled={isLevelingUp || gbofBalance < 1}
            >
              <div className="btn-content">
                {isLevelingUp ? (
                  <>
                    <FaRocket className="spin" />
                    <span>DEPOSITING...</span>
                  </>
                ) : (
                  <>
                    <FaFire className="gbof-btn-icon" />
                    <span>DEPOSIT 1 GBOF</span>
                  </>
                )}
              </div>
              <div className="btn-glow" />
            </button>


          </div>
        )}

        {!isMaxLevel && (
          <div className="balance-display">
            <FaFire className="balance-icon" />
            <span>Your GBOF: <strong>{gbofBalance}</strong></span>
          </div>
        )}

        {!isMaxLevel && gbofBalance < 1 && (
          <div className="insufficient-notice">
            You need at least 1 GBOF to level up
          </div>
        )}
      </div>

      {/* Level Milestones - Compact view for effective 0-12 system */}
      <div className="level-milestones">
        <div className="milestones-title">Level Milestones</div>
        <div className="milestones-track">
          {LEVEL_MILESTONES.map((targetLvl) => {
            const tier = getTierWithIcon(targetLvl);
            const isCurrentLevel = level === targetLvl;
            const isPast = level > targetLvl;
            const isNext = level < targetLvl && !isPast;

            return (
              <div
                key={targetLvl}
                className={`milestone-node ${isPast ? 'past' : ''} ${isCurrentLevel ? 'current' : ''} ${isNext ? 'future' : ''}`}
                style={{
                  borderColor: isPast || isCurrentLevel ? tier.color : '#333',
                  background: isPast || isCurrentLevel ? `${tier.color}15` : 'transparent'
                }}
              >
                <span className="node-level">{targetLvl}</span>
                <span className="node-tier" style={{ color: tier.color }}>{getLevelName(targetLvl)}</span>
                {isCurrentLevel && (
                  <div className="current-indicator" style={{ background: tier.color }} />
                )}
              </div>
            );
          })}
        </div>

      </div>

      {/* All Level Names */}
      <div className="level-names">
        <div className="level-names-header">
          <div className="level-names-title">All Level Names (0-11)</div>
          <div className="level-names-subtitle">Every level gets a title — climb the list.</div>
        </div>
        <div className="level-names-grid">
          {LEVEL_NAMES.map((name, index) => {
            const tier = getLevelTier(index);
            const isCurrent = level === index;
            return (
              <div
                key={name}
                className={`level-name-card ${isCurrent ? 'current' : ''}`}
                style={{ borderColor: isCurrent ? tier.color : 'rgba(212, 165, 32, 0.15)' }}
              >
                <span className="level-name-number" style={{ color: tier.color }}>
                  {index}
                </span>
                <span className="level-name-text">{name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LevelProgress;
